/**
 * PendingInvitesDialog — iter247.
 *
 * Shown right after group creation when some typed phone numbers have no
 * SplitEasy account. Lets the admin, per number:
 *   • add them anyway as a Ghost member (name required, auto-merges at signup)
 *   • send a WhatsApp invite with the group join link
 */
import { useState } from 'react';
import { MessageCircle, UserPlus, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../lib/api';
import { sendSystemWaInvite } from '../lib/systemWaInvite';

export const waInviteUrl = (phone, groupName, code) => {
  const digits = (phone || '').replace(/\D/g, '');
  const target = digits.length === 10 ? `91${digits}` : digits;
  const link = `${window.location.origin}/join/${code}`;
  const text = `Hey! Join our group "${groupName}" on SplitEasy to track & split expenses: ${link}`;
  return `https://wa.me/${target}?text=${encodeURIComponent(text)}`;
};

export default function PendingInvitesDialog({ open, onClose, group, phones }) {
  const [names, setNames] = useState({});
  const [added, setAdded] = useState(new Set());
  const [busyPhone, setBusyPhone] = useState(null);
  const [inviteCode, setInviteCode] = useState('');

  const ensureInviteCode = async () => {
    if (inviteCode) return inviteCode;
    const res = await api.post(`/groups/${group.id}/invite`);
    const c = res.data?.code;
    setInviteCode(c);
    return c;
  };

  const handleGhostAdd = async (phone) => {
    const name = (names[phone] || '').trim();
    if (!name) {
      toast.error('Enter their name first');
      return;
    }
    setBusyPhone(phone);
    try {
      await api.post(`/groups/${group.id}/shadow-member`, { name, phone });
      setAdded((prev) => new Set(prev).add(phone));
      toast.success(`${name} added — they'll auto-merge when they sign up`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add');
    } finally {
      setBusyPhone(null);
    }
  };

  const handleWhatsApp = async (phone) => {
    setBusyPhone(phone);
    try {
      // Preferred: system sends from the SplitEasy Meta WhatsApp number
      await sendSystemWaInvite(group.id, phone, (names[phone] || '').trim());
      toast.success(`Invite sent to ${phone} from SplitEasy's WhatsApp ✓`);
    } catch (e) {
      // Fallback: open the admin's own WhatsApp with the link pre-filled
      try {
        const code = await ensureInviteCode();
        window.open(waInviteUrl(phone, group.name, code), '_blank');
        toast.info('System WhatsApp unavailable — opened your WhatsApp instead');
      } catch {
        toast.error('Could not generate invite link');
      }
    } finally {
      setBusyPhone(null);
    }
  };

  if (!group) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="pending-invites-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {phones.length} number{phones.length === 1 ? '' : 's'} not on SplitEasy yet
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Group <span className="font-semibold text-foreground">"{group.name}"</span> was created, but these
          numbers have no account. Add them now as members (they'll claim their spot when they sign up), or
          send them a WhatsApp invite.
        </p>
        <div className="space-y-3">
          {phones.map((phone) => (
            <div key={phone} className="p-3 rounded-xl border border-border space-y-2" data-testid={`pending-invite-row-${phone}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold tabular-nums text-sm">{phone}</p>
                {added.has(phone) ? (
                  <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> Added
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleWhatsApp(phone)}
                    data-testid={`wa-invite-${phone}`}
                    className="flex items-center gap-1 text-xs font-bold text-green-600 hover:text-green-700 transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp invite
                  </button>
                )}
              </div>
              {!added.has(phone) && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Their name"
                    value={names[phone] || ''}
                    onChange={(e) => setNames({ ...names, [phone]: e.target.value })}
                    className="h-9 rounded-lg text-sm"
                    data-testid={`ghost-name-${phone}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => handleGhostAdd(phone)}
                    disabled={busyPhone === phone}
                    data-testid={`ghost-add-${phone}`}
                    className="rounded-full h-9 text-xs shrink-0"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1" /> Add to group
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
        <Button variant="outline" onClick={onClose} className="w-full rounded-full" data-testid="pending-invites-done-btn">
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}
